// Shipping providers hub (option B, approved 2026-09-15): purchase channels on
// top, carriers & services below, then the per-product weights and boxes. PEXIFY quotes every ACTIVE channel and buys the
// cheapest label, so this is the single place a channel is connected, tested and
// switched on or off.
import { useQuery } from '@tanstack/react-query'
import { Card } from '../../../components/ui/card'
import { API_BASE } from '../../../lib/api'
import { RESELLER_CHANNEL_NAMES } from '../../../lib/shippingProviders'
import { useT } from '../../../i18n'
import { shippingProvidersI18n } from '../../../i18n/admin/shipping-providers'
import { AddressCheckCard } from './AddressCheckCard'
import { CarriersSettings } from './CarriersSettings'
import { ProviderCard } from './ProviderCard'
import { CREDENTIALS_QUERY_KEY, type CarrierCredential, type ProviderDef } from './provider-hub.lib'

// Roles come from the dictionary (shippingProvidersI18n.roles[id]).
const PROVIDERS: Omit<ProviderDef, 'role'>[] = [
  {
    id: 'avocore',
    label: RESELLER_CHANNEL_NAMES.avocore,
    fields: ['api_key'],
    hasBalance: true,
  },
  {
    id: 'byelabel',
    label: RESELLER_CHANNEL_NAMES.byelabel,
    fields: ['client_id', 'client_secret', 'workspace_id'],
    hasBalance: true,
  },
  {
    id: 'shipdad',
    label: RESELLER_CHANNEL_NAMES.shipdad,
    fields: ['api_key'],
    hasBalance: false,
  },
  {
    id: 'uniuni',
    label: 'UniUni',
    fields: ['access_token'],
    hasBalance: false,
  },
  {
    id: 'stamps',
    label: 'Stamps.com / SERA',
    fields: ['client_id', 'client_secret', 'refresh_token'],
    hasBalance: true,
  },
  {
    id: 'shipstation',
    label: 'ShipStation',
    fields: ['api_key', 'legacy_api_key', 'legacy_api_secret'],
    // ShipStation's labels draw on its connected postage account (Stamps.com
    // here); the card names that account when the server reports it.
    hasBalance: true,
  },
  {
    id: 'usps',
    label: 'USPS (direct)', // replaced by t.uspsDirectLabel at render
    fields: ['client_id', 'client_secret', 'eps_payment_account_number'],
    hasBalance: false,
  },
]

function SectionHeading({ id, title, hint }: { id: string; title: string; hint: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h2 id={id} className="text-base font-semibold text-[var(--color-text)]">
        {title}
      </h2>
      <span className="text-xs text-[var(--color-text-muted)]">{hint}</span>
    </div>
  )
}

export function ShippingHub() {
  const t = useT(shippingProvidersI18n)
  const providers: ProviderDef[] = PROVIDERS.map((p) => ({
    ...p,
    label: p.id === 'usps' ? t.uspsDirectLabel : p.label,
    role: t.roles[p.id],
  }))
  const { data, isLoading, isError } = useQuery({
    queryKey: CREDENTIALS_QUERY_KEY,
    queryFn: async (): Promise<{ items: CarrierCredential[] }> => {
      const res = await fetch(`${API_BASE}/admin/carrier-credentials`)
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
  })
  const rows = new Map((data?.items ?? []).map((row) => [row.carrier_id, row]))

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-[var(--color-text)]">{t.hub.title}</h1>
        <p className="mt-0.5 max-w-3xl text-sm text-[var(--color-text-muted)]">
          {t.hub.description}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2.5 text-[13px] text-[var(--color-text-muted)]">
        <span className="inline-flex items-center gap-1.5 font-medium text-[var(--color-text)]">
          <span className="h-2 w-2 rounded-full bg-[var(--color-signal)]" aria-hidden />
          {t.hub.cheapestRule}
        </span>
        <span>{t.hub.offRule}</span>
        <span>{t.hub.internalRule}</span>
      </div>

      <section aria-labelledby="channels-heading" className="space-y-3">
        <SectionHeading id="channels-heading" title={t.hub.channelsTitle} hint={t.hub.channelsHint} />
        {isLoading ? (
          <Card className="p-8 text-center text-sm text-[var(--color-text-muted)]">{t.hub.loading}</Card>
        ) : isError ? (
          <Card className="p-8 text-center text-sm text-[var(--color-danger)]">
            {t.hub.loadError}
          </Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {providers.map((provider) => (
              <ProviderCard key={provider.id} provider={provider} row={rows.get(provider.id)} />
            ))}
          </div>
        )}
      </section>

      <section aria-labelledby="carriers-heading" className="space-y-3">
        <SectionHeading id="carriers-heading" title={t.hub.carriersTitle} hint={t.hub.carriersHint} />
        <CarriersSettings />
      </section>

      <section aria-labelledby="tools-heading" className="space-y-3">
        <SectionHeading id="tools-heading" title={t.hub.addressTitle} hint={t.hub.addressHint} />
        <div className="max-w-xl">
          <AddressCheckCard />
        </div>
      </section>
    </div>
  )
}
